import { apiClient } from '@/api/client'
import axios from 'axios'
import type {
  Libro,
  LibroIn,
  LibroPublicInfo,
  Codigo,
  CampoFormulario,
  CampoIn,
  Registro,
  LibroStats,
  ValidarCodigoResponse,
  FormularioConfig,
  CanjeResult,
  DescargaInfo,
  PaginatedResponse,
} from '../types'

const BASE = '/libro-qr'
const PUBLIC_BASE = '/api/v2/libro-qr/public'

// ============================================================================
// Admin — Libros
// ============================================================================

export async function getLibros(params?: { search?: string; page?: number; page_size?: number }) {
  const { data } = await apiClient.get<PaginatedResponse<Libro>>(`${BASE}/libros/`, { params })
  return data
}

export async function getLibro(id: number) {
  const { data } = await apiClient.get<Libro>(`${BASE}/libros/${id}/`)
  return data
}

export async function createLibro(payload: LibroIn) {
  const { data } = await apiClient.post<Libro>(`${BASE}/libros/`, payload)
  return data
}

export async function updateLibro(id: number, payload: LibroIn) {
  const { data } = await apiClient.put<Libro>(`${BASE}/libros/${id}/`, payload)
  return data
}

export async function deleteLibro(id: number) {
  const { data } = await apiClient.delete(`${BASE}/libros/${id}/`)
  return data
}

export async function toggleLibroStatus(id: number) {
  const { data } = await apiClient.post<Libro>(`${BASE}/libros/${id}/toggle-status/`)
  return data
}

// ============================================================================
// Admin — Códigos
// ============================================================================

export async function getCodigos(libroId: number, params?: { estado?: string; search?: string }) {
  const { data } = await apiClient.get<Codigo[]>(`${BASE}/libros/${libroId}/codigos/`, { params })
  return data
}

export async function generarCodigos(libroId: number, cantidad: number) {
  const { data } = await apiClient.post(`${BASE}/libros/${libroId}/codigos/generar/`, { cantidad })
  return data
}

export async function toggleCodigoStatus(libroId: number, codigoId: number) {
  const { data } = await apiClient.post<Codigo>(`${BASE}/libros/${libroId}/codigos/${codigoId}/toggle/`)
  return data
}

export async function deleteCodigo(libroId: number, codigoId: number) {
  const { data } = await apiClient.delete(`${BASE}/libros/${libroId}/codigos/${codigoId}/`)
  return data
}

// ============================================================================
// Admin — Campos
// ============================================================================

export async function getCampos(libroId: number) {
  const { data } = await apiClient.get<CampoFormulario[]>(`${BASE}/libros/${libroId}/campos/`)
  return data
}

export async function createCampo(libroId: number, payload: CampoIn) {
  const { data } = await apiClient.post<CampoFormulario>(`${BASE}/libros/${libroId}/campos/`, payload)
  return data
}

export async function updateCampo(libroId: number, campoId: number, payload: CampoIn) {
  const { data } = await apiClient.put<CampoFormulario>(`${BASE}/libros/${libroId}/campos/${campoId}/`, payload)
  return data
}

export async function deleteCampo(libroId: number, campoId: number) {
  const { data } = await apiClient.delete(`${BASE}/libros/${libroId}/campos/${campoId}/`)
  return data
}

// ============================================================================
// Admin — Analytics & Exportaciones
// ============================================================================

export async function getStats(libroId: number) {
  const { data } = await apiClient.get<LibroStats>(`${BASE}/libros/${libroId}/stats/`)
  return data
}

export async function getRegistros(libroId: number, params?: { tipo?: string; search?: string }) {
  const { data } = await apiClient.get<Registro[]>(`${BASE}/libros/${libroId}/registros/`, { params })
  return data
}

export function getExportarRegistrosUrl(libroId: number) {
  return `${BASE}/libros/${libroId}/exportar-registros/`
}

export function getExportarCodigosUrl(libroId: number, params?: { search?: string; status?: string }) {
  const searchParams = new URLSearchParams()
  if (params?.search) searchParams.set('search', params.search)
  if (params?.status) searchParams.set('status', params.status)
  const qs = searchParams.toString()
  return `${BASE}/libros/${libroId}/exportar-codigos/${qs ? `?${qs}` : ''}`
}

export function getDescargarQRUrl(libroId: number) {
  return `${BASE}/libros/${libroId}/descargar-qr/`
}

// ============================================================================
// Público — Sin auth
// ============================================================================

const publicClient = axios.create()

export async function getLibroPublicInfo(uuid: string) {
  const { data } = await publicClient.get<LibroPublicInfo>(
    `${PUBLIC_BASE}/${uuid}/info/`
  )
  return data
}

export async function validarCodigo(uuid: string, codigo: string) {
  const { data } = await publicClient.post<ValidarCodigoResponse>(
    `${PUBLIC_BASE}/${uuid}/validar/`,
    { codigo }
  )
  return data
}

export async function getFormularioConfig(uuid: string) {
  const { data } = await publicClient.get<FormularioConfig>(
    `${PUBLIC_BASE}/${uuid}/formulario/`
  )
  return data
}

export async function canjearCodigo(uuid: string, token: string, datos: Record<string, string>) {
  const { data } = await publicClient.post<CanjeResult>(
    `${PUBLIC_BASE}/${uuid}/canjear/?token=${encodeURIComponent(token)}`,
    { datos }
  )
  return data
}

export async function getDescargaInfo(downloadToken: string) {
  const { data } = await publicClient.get<DescargaInfo>(
    `${PUBLIC_BASE}/descarga/${encodeURIComponent(downloadToken)}/?info=1`
  )
  return data
}

export function getDescargaUrl(downloadToken: string) {
  return `${PUBLIC_BASE}/descarga/${encodeURIComponent(downloadToken)}/`
}

export async function solicitarCodigo(uuid: string, datos: Record<string, string>, codigoUsado?: string) {
  const params = codigoUsado ? `?codigo_usado=${encodeURIComponent(codigoUsado)}` : ''
  const { data } = await publicClient.post<CanjeResult>(
    `${PUBLIC_BASE}/${uuid}/solicitar/${params}`,
    { datos }
  )
  return data
}
